<template>
  <div>
    <b-form>
      <div class="form-group flex-inline-form-lg">
        <b-input-group class="fill-width">
          <b-input-group-text slot="prepend">
            <i class="fa fa-search"></i>
          </b-input-group-text>
          <b-form-input
            style="height:auto;"
            type="text"
            placeholder="Search vehicle by reg number"
            @input="onChangeSearchQuery($event)"
            v-model="searchQuery"></b-form-input>
        </b-input-group>
        <b-input-group v-if="!hideCompanySelect">
          <company-multi-select
              :multiple="false"
              having="vehicle"
              :init-selection-id="companyIdQuery"
              @on-selected="onCompanySelected"
            />
        </b-input-group>
        <b-input-group style="min-width: 150px;">
          <b-form-select
            style="height:auto;"
            v-model="sortType"
            @change="onChangeSortType($event)"
            :options="sortOptions"></b-form-select>
        </b-input-group>
        <b-input-group>
          <b-button
            @click="createVehicle()"
            variant="success"
            v-if="canICreateVehicle()"
            title="Create new vehicle">
            New vehicle &nbsp;<i class="fa fa-plus"></i>
          </b-button>
        </b-input-group>
      </div>
    </b-form>
    <b-overlay :show="isVehicleListLoading">
      <b-list-group class="no-box" v-if="totalRows">
        <b-list-group-item
          v-for="vehicle in vehicles"
          :key="vehicle.id">
          <div class="pull-left">
            <router-link :to="'vehicles/' + vehicle.id">
              <strong>{{vehicle.reg_no}}</strong>
              <b-badge pill variant="success" v-if="vehicle.gps_devices.length">GPS</b-badge>
            </router-link>
            <p class="mb-1">{{vehicle.company.name}}</p>
            <small :title="formatDateTime(vehicle.created_at)">Created at: {{formatDateDistance(vehicle.created_at)}}</small>
          </div>
          <div v-if="hasAccessVehicle(vehicle)" class="pull-right">
            <b-button v-if="canIEditVehicle(vehicle)" variant="outline-primary" @click="editVehicle(vehicle.id)">
              <i class="fa fa-pencil-square-o"></i>&nbsp; Edit
            </b-button>
            <b-dropdown v-if="canIDeleteVehicle(vehicle)" variant="outline-primary" class="m-md-2">
              <i class="fa fa-gear" slot="button-content"></i>
              <b-dropdown-item class="text-warning">Disable</b-dropdown-item>
              <b-dropdown-item @click="deleteVehicle(vehicle.id)" class="text-danger">Remove</b-dropdown-item>
            </b-dropdown>
          </div>
        </b-list-group-item>
      </b-list-group>
      <div style="height:150px;" v-if="isVehicleListLoading && totalRows === 0"></div>
    </b-overlay>
    <b-pagination class="mt-3" align="center" size="md" :total-rows="totalRows" v-model="currentPage" :per-page="query_count" v-if="totalRows">
    </b-pagination>
    <b-alert show v-if="isFetched & totalRows === 0">There's no vehicle inside this company.</b-alert>
    <div>
      <b-modal
        id="delete_vehicle"
        title="Delete Vehicle"
        header-bg-variant="danger"
        ok-variant="danger"
        @ok="confirmedDeleteVehicle(deleteVehicleId)">
        <p class="my-4">Are you sure want to delete this vehicle?</p>
      </b-modal>
    </div>
  </div>
</template>
<script>
import dateMixin from '@/mixins/date';
import vehicleMixin from '@/mixins/vehicle';
import routeQuery from '@/mixins/route_query';
import { throttle, isEmpty, uniqBy } from 'lodash';

import CompanyMultiSelect from '@/components/CompanyMultiSelect';

export default {
  name: 'vehicle_table',
  mixins: [dateMixin, vehicleMixin, routeQuery],
  components: {
    CompanyMultiSelect,
  },
  props: {
    companyId: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      deleteVehicleId: null,
      loaded: false,
      disableEdit: false,
      disableCreate: false,
      searchQuery: '',
      currentPage: 1,
      sortOptions: [
        { value: null, text: 'Sort By', disabled: true },
        { value: 'CREATED_AT.DESC', text: 'Newer' },
        { value: 'CREATED_AT.ASC', text: 'Older' },
      ],
      sortType: null,
    };
  },
  created() {
    this.$store.commit('SET_BREADCRUMB_ITEMS', [
      { to: '', name: 'Home' },
      { to: 'admin', name: 'Admin' },
      { to: 'vehicles', name: 'Vehicles' },
    ]);
    this.$store.commit('vehicles/CLEAR_QUERY');
    this.initFromUrl();
  },
  computed: {
    hideCompanySelect() {
      return this.companyId !== '';
    },
    companyIdQuery() {
      return this.getRouteQuery('company_id', '');
    },
    vehicles() {
      const vehicles = this.$store.getters['vehicles/vehicles'];
      return uniqBy(vehicles, 'id');
    },
    count_all() {
      return this.$store.getters['vehicles/count_all'];
    },
    query_count() {
      return this.$store.getters['vehicles/query_count'];
    },
    totalRows() {
      return this.count_all;
    },
    isNotEmpty() {
      return !(this.count_all === 0 && this.searchQuery === '');
    },
    userData() {
      return this.$store.getters.userData.account.access.MAIN.VEHICLE;
    },
    is_data_loaded() {
      return this.$store.getters['vehicles/dataLoaded'];
    },
    vehicleGetters() {
      return this.$store.getters['vehicles/loadVehicle'];
    },
    isVehicleListLoading() {
      return this.vehicleGetters === 'LOADING';
    },
    isFetched() {
      return this.vehicleGetters === 'LOADED';
    },
  },
  watch: {
    currentPage(newValue) {
      if (this.is_data_loaded) {
        this.$store.commit('vehicles/SET_QUERY_PAGE', newValue);
        this.loadData();
      }
    },
  },
  methods: {
    initFromUrl() {
      let fCompanyId = this.getRouteQuery('company_id');
      const sort = this.getRouteQuery('sort');
      const search = this.getRouteQuery('q');

      if (this.companyId !== '') {
        fCompanyId = this.companyId;
      }

      if (typeof fCompanyId !== 'undefined') {
        this.setCompanyId({ id: fCompanyId }, true);
      }
      if (sort !== undefined) {
        this.onChangeSortType(sort, true);
      }
      if (search !== undefined) {
        this.onChangeSearchQuery(search, true);
      }

      this.loadInfoAndData();
    },
    updateSearchQuery: throttle((context, searchTerm) => {
      const searchQuery = searchTerm.trim();
      context.$store.commit('vehicles/SET_QUERY_SEARCH', searchQuery);
      context.loadInfoAndData();
    }, 500, {
      leading: false,
    }),
    loadInfoAndData() {
      this.$store.dispatch('vehicles/LOAD_INFO');
      this.loadData();
    },
    loadData() {
      return this.$store.dispatch('vehicles/LOAD_DATA');
    },
    editVehicle(vehicleId) {
      this.$router.push(`/admin/vehicles/${vehicleId}/edit`);
    },
    createVehicle() {
      this.$router.push('/admin/vehicles/create');
    },
    deleteVehicle(vehicleId) {
      this.deleteVehicleId = vehicleId;
      this.$bvModal.show('delete_vehicle');
    },
    confirmedDeleteVehicle(vehicleId) {
      this.$store.dispatch('vehicles/DELETE_DATA', vehicleId).then((ok) => {
        if (ok) this.loadInfoAndData();
      });
    },
    onCompanySelected(selected) {
      this.setCompanyId(selected);
      this.loadInfoAndData();
    },
    setCompanyId(selected, fromInit = false) {
      if (typeof selected !== 'undefined' && selected !== null) {
        this.$store.commit('vehicles/SET_COMPANY_ID', selected.id);
        const currId = this.getRouteQuery('company_id', '');
        if (!fromInit && selected.id !== currId) {
          this.setRouteQuery('company_id', selected.id);
        }
      } else {
        this.$store.commit('vehicles/SET_COMPANY_ID', null);
        if (!fromInit) {
          this.removeRouteQuery('company_id');
        }
      }
    },
    onChangeSortType(newValue, fromInit = false) {
      if (!fromInit) {
        this.setRouteQuery('sort', newValue);
      }
      this.$store.commit('vehicles/SET_QUERY_SORT', newValue);
      this.sortType = newValue;

      if (!fromInit) {
        this.loadData();
      }
    },
    onChangeSearchQuery(newValue, fromInit = false) {
      this.$store.commit('vehicles/SET_QUERY_SEARCH', newValue);
      if (!fromInit) {
        if (!isEmpty(newValue)) {
          this.setRouteQuery('q', newValue);
        } else {
          this.removeRouteQuery('q');
        }
      }
      this.searchQuery = newValue;

      if (!fromInit) {
        this.updateSearchQuery(this, newValue);
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/VehicleTable.vue